import { matchRank, type SearchResults } from '@peruselab/api-client/moleculeSearch';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import { moleculeComponents } from '@peruselab/structure-editor/domain/reactionGraph';
import { moleculeEdges } from '@peruselab/structure-editor/domain/documentSelectors';
import { elementSymbol } from '@peruselab/structure-editor/chemistry/elements';

/** #701 -- at most this many molecules on the paper are searched (left to right), so a crowded
 *  drawing can't fire an unbounded number of requests. */
export const MAX_QUERY_MOLECULES = 8;

/** One molecule on the paper, as a query of its own. */
export interface QueryMolecule {
  /** Its chemistry only (atoms, bonds, charges, by the drawing's own ids): unchanged by moves or
   *  by edits to other molecules, so an unchanged molecule's results can be reused. */
  readonly key: string;
  /** The drawing cut down to this molecule, for the search request. */
  readonly document: DrawingDocument;
  /** Its molecular formula (Hill order), to say on a result card what it matched. */
  readonly formula: string;
}

const SUBSCRIPTS = '₀₁₂₃₄₅₆₇₈₉';

/** Usual valences, for the implicit hydrogens in a label's formula (the formula only names the
 *  query on a result card; the search itself sends the structure, not this). */
const TYPICAL_VALENCE: Readonly<Record<number, number>> = {
  5: 3,
  6: 4,
  7: 3,
  8: 2,
  9: 1,
  14: 4,
  15: 3,
  16: 2,
  17: 1,
  35: 1,
  53: 1
};
const GAINS_WITH_CHARGE = new Set([7, 8, 15, 16]); // N+ / O+ / P+ / S+ bond once more

/** Implicit hydrogens on each atom of `document` (by its usual valence, less its bond orders). */
function implicitHydrogens(document: DrawingDocument): ReadonlyMap<string, number> {
  const { structure } = document;
  const bonded = new Map<string, number>();
  for (const edge of structure.graph.edges) {
    const order = structure.bonds[edge.id]?.order ?? 1;
    for (const end of [edge.source, edge.target]) bonded.set(end, (bonded.get(end) ?? 0) + order);
  }
  return new Map(
    structure.graph.nodes.map(({ id }) => {
      const atomicNumber = structure.atoms[id]?.atomicNumber ?? 0;
      const charge = structure.formalCharges[id] ?? 0;
      const base = TYPICAL_VALENCE[atomicNumber];
      if (base === undefined) return [id, 0];
      const valence = GAINS_WITH_CHARGE.has(atomicNumber) ? base + charge : base - Math.abs(charge);
      return [id, Math.max(0, valence - (bonded.get(id) ?? 0))];
    })
  );
}

/** Hill order: C, then H, then the rest alphabetically (all alphabetical without carbon), with
 *  subscript counts -- "C₂H₆O". */
export function hillFormula(
  atoms: readonly { readonly atomicNumber: number; readonly implicitHydrogenCount: number }[]
) {
  const counts = new Map<string, number>();
  const add = (symbol: string, count: number) => counts.set(symbol, (counts.get(symbol) ?? 0) + count);
  for (const atom of atoms) {
    add(elementSymbol(atom.atomicNumber), 1);
    if (atom.implicitHydrogenCount > 0) add('H', atom.implicitHydrogenCount);
  }
  const symbols = [...counts.keys()].sort((a, b) => a.localeCompare(b));
  const ordered = counts.has('C')
    ? ['C', ...(counts.has('H') ? ['H'] : []), ...symbols.filter((s) => s !== 'C' && s !== 'H')]
    : symbols;
  const subscript = (count: number) =>
    count === 1 ? '' : [...String(count)].map((digit) => SUBSCRIPTS[Number(digit)]).join('');
  return ordered.map((symbol) => symbol + subscript(counts.get(symbol)!)).join('');
}

/**
 * #701 -- the molecules on the paper (loose or in a reaction box), each as its own query, left to
 * right, at most MAX_QUERY_MOLECULES. A drawing of several molecules searched as one query would
 * match almost nothing; searched one by one, each finds its own matches.
 *
 * #1086 (owner) -- a salt is searched as one structure too, first: when two or more pieces are
 * charged and their charges add up to zero (CH₃CH₂O⁻ + Na⁺, Ca²⁺ + 2 Cl⁻), those pieces together
 * are one more query, so the saved salt is found as Identical. Neutral pieces stay on their own.
 */
export function queryMolecules(document: DrawingDocument, max = MAX_QUERY_MOLECULES): QueryMolecule[] {
  const { structure } = document;
  const { coordinates } = document.depiction.coordinateSet;
  const components = moleculeComponents(
    structure.graph.nodes.map((node) => node.id),
    moleculeEdges(structure),
    (id) => coordinates[id]
  ).sort((a, b) => a.bounds.minX - b.bounds.minX || a.bounds.minY - b.bounds.minY);
  const hydrogens = implicitHydrogens(document);
  const pieces = components.map(({ atomIds }) => queryMoleculeOf(document, atomIds, hydrogens));
  const saltAtoms = saltAtomIds(document, components);
  const salt = saltAtoms ? [queryMoleculeOf(document, saltAtoms, hydrogens)] : [];
  return [...salt, ...pieces].slice(0, max);
}

/** #1086: the atoms of the charged pieces, when there are two or more and their charges balance. */
function saltAtomIds(
  document: DrawingDocument,
  components: readonly { readonly atomIds: readonly string[] }[]
): readonly string[] | null {
  const charge = (atomIds: readonly string[]) =>
    atomIds.reduce((sum, id) => sum + (document.structure.formalCharges[id] ?? 0), 0);
  const charged = components.filter(({ atomIds }) => charge(atomIds) !== 0);
  if (charged.length < 2 || charged.reduce((sum, { atomIds }) => sum + charge(atomIds), 0) !== 0) return null;
  return charged.flatMap(({ atomIds }) => atomIds);
}

/**
 * One molecule of the drawing (its atom ids) as a query of its own: the drawing cut down to it,
 * a chemistry-only key, and its formula. #704: also used for the molecules in a reaction box.
 */
export function queryMoleculeOf(
  document: DrawingDocument,
  atomIds: readonly string[],
  hydrogens: ReadonlyMap<string, number> = implicitHydrogens(document)
): QueryMolecule {
  const { structure } = document;
  const inMolecule = new Set(atomIds);
  const nodes = structure.graph.nodes.filter((node) => inMolecule.has(node.id));
  const edges = moleculeEdges(structure).filter((edge) => inMolecule.has(edge.source) && inMolecule.has(edge.target));
  const pick = <T>(record: Readonly<Record<string, T>>, ids: readonly string[]) =>
    Object.fromEntries(ids.filter((id) => id in record).map((id) => [id, record[id]!]));
  const nodeIds = nodes.map((node) => node.id);
  const edgeIds = edges.map((edge) => edge.id);
  const chemistry = {
    atoms: pick(structure.atoms, nodeIds),
    bonds: pick(structure.bonds, edgeIds),
    formalCharges: pick(structure.formalCharges, nodeIds)
  };
  return {
    key: JSON.stringify([nodes, edges, chemistry.atoms, chemistry.bonds, chemistry.formalCharges]),
    document: pieceOnly(document, { ...structure, graph: { ...structure.graph, nodes, edges }, ...chemistry }),
    formula: hillFormula(
      nodeIds.map((id) => ({
        atomicNumber: structure.atoms[id]?.atomicNumber ?? 0,
        implicitHydrogenCount: hydrogens.get(id) ?? 0
      }))
    )
  };
}

type ResultWithMatches = SearchResults & { readonly matchedByText?: Readonly<Record<string, string>> };

const better = (a: { category: string; tanimotoScore: number }, b: { category: string; tanimotoScore: number }) =>
  matchRank(a.category) - matchRank(b.category) || b.tanimotoScore - a.tanimotoScore;

/**
 * #701 -- one answer from each molecule's own search: every list joined (an entry found by
 * several molecules kept once, at its best match), and -- when there was more than one molecule
 * -- each result molecule labelled with which of them it matched (`describe` the formulas).
 */
export function mergeSearchResults(
  parts: readonly { readonly formula: string; readonly results: SearchResults }[],
  describe: (formulas: readonly string[]) => string
): ResultWithMatches {
  const matched = new Map<string, string[]>();
  const note = (moleculeId: string, formula: string) => {
    const formulas = matched.get(moleculeId) ?? [];
    if (!formulas.includes(formula)) matched.set(moleculeId, [...formulas, formula]);
  };
  const joined = <T extends { category: string; tanimotoScore: number }>(
    lists: readonly (readonly T[])[],
    id: (entry: T) => string
  ): T[] => {
    const best = new Map<string, T>();
    for (const entry of lists.flat()) {
      const current = best.get(id(entry));
      if (!current || better(current, entry) > 0) best.set(id(entry), entry);
    }
    return [...best.values()];
  };
  for (const { formula, results } of parts) {
    for (const molecule of results.molecules) note(molecule.moleculeId, formula);
    for (const container of [...results.drawings, ...results.reactions, ...results.illustrations])
      if (container.sourceMoleculeId) note(container.sourceMoleculeId, formula);
  }
  const lists = <K extends keyof SearchResults>(key: K) => parts.map((part) => part.results[key]);
  return {
    molecules: joined(lists('molecules'), (entry) => entry.moleculeId),
    drawings: joined(lists('drawings'), (entry) => `${entry.documentId}:${entry.sourceMoleculeId}`),
    reactions: joined(lists('reactions'), (entry) => `${entry.documentId}:${entry.sourceMoleculeId}`),
    illustrations: joined(lists('illustrations'), (entry) => `${entry.documentId}:${entry.sourceMoleculeId}`),
    ...(parts.length > 1
      ? { matchedByText: Object.fromEntries([...matched].map(([id, formulas]) => [id, describe(formulas)])) }
      : {})
  };
}

/**
 * #1066 step 3: the drawing cut down to one molecule throughout -- its stereo, depiction and derived
 * properties name only its own atoms and bonds, and it carries no annotations -- so it can be saved
 * as an Isomer of its own (remapStructureIds, toIsomerCreatePayload) as well as searched for.
 */
function pieceOnly(document: DrawingDocument, structure: DrawingDocument['structure']): DrawingDocument {
  const atomIds = new Set(structure.graph.nodes.map((node) => node.id));
  const bondIds = new Set(structure.graph.edges.map((edge) => edge.id));
  const onAtoms = <T>(record: Readonly<Record<string, T>> | undefined) =>
    record && Object.fromEntries(Object.entries(record).filter(([id]) => atomIds.has(id)));
  const onBonds = <T>(record: Readonly<Record<string, T>> | undefined) =>
    record && Object.fromEntries(Object.entries(record).filter(([id]) => bondIds.has(id)));
  const { depiction } = document;
  const { coordinateSet } = depiction;
  return {
    ...document,
    structure: {
      ...structure,
      stereochemistry: {
        tetrahedral: onAtoms(structure.stereochemistry.tetrahedral)!,
        doubleBonds: onBonds(structure.stereochemistry.doubleBonds)!
      }
    },
    depiction: {
      ...depiction,
      coordinateSet: {
        ...coordinateSet,
        coordinates: onAtoms(coordinateSet.coordinates)!,
        ...(coordinateSet.depths ? { depths: onAtoms(coordinateSet.depths)! } : {})
      },
      atomLabels: onAtoms(depiction.atomLabels)!,
      bondStyles: onBonds(depiction.bondStyles)!,
      bondLengthOverrides: onBonds(depiction.bondLengthOverrides)!,
      ...(depiction.atomAppearance ? { atomAppearance: onAtoms(depiction.atomAppearance)! } : {}),
      ...(depiction.contractedGroups ? { contractedGroups: onAtoms(depiction.contractedGroups)! } : {}),
      ...(depiction.bondAppearance ? { bondAppearance: onBonds(depiction.bondAppearance)! } : {}),
      ...(depiction.cycleFills
        ? { cycleFills: depiction.cycleFills.filter((fill) => fill.atomIds.every((id) => atomIds.has(id))) }
        : {})
    },
    annotations: [],
    derivedPropertySets: document.derivedPropertySets.map((set) => {
      switch (set.kind) {
        case 'aromaticity':
          return {
            ...set,
            atomIds: set.atomIds.filter((id) => atomIds.has(id)),
            bondIds: set.bondIds.filter((id) => bondIds.has(id))
          };
        case 'cip':
          return {
            ...set,
            atomDescriptors: onAtoms(set.atomDescriptors)!,
            bondDescriptors: onBonds(set.bondDescriptors)!
          };
        case 'partial-charge':
          return { ...set, charges: onAtoms(set.charges)! };
        default:
          return set;
      }
    }) as DrawingDocument['derivedPropertySets']
  };
}
